/**
 * Fee-module shared types. Add new fee-related interfaces here as the
 * module grows (FeePlan, FeeInvoice, FeePayment, etc.). Keeps
 * revenue/expense types uncontaminated.
 */

export interface FeeCategory {
  _id: string;
  schoolId: string;
  code: string;
  name: string;
  description?: string;
  isOptional: boolean;
  isRefundable: boolean;
  taxRate?: number | null;
  displayOrder: number;
  isActive: boolean;
  /** Ledger account it posts to; null = the suggested one. */
  glAccountCode?: string | null;
  suggestedAccountCode?: string;
  createdAt: string;
  updatedAt: string;
}

export interface FeeCategoryInput {
  code: string;
  name: string;
  description?: string;
  isOptional?: boolean;
  isRefundable?: boolean;
  taxRate?: number | null;
  displayOrder?: number;
  /** Sent only by people allowed to choose ledger accounts. */
  glAccountCode?: string | null;
}

// ── Fee Plans ────────────────────────────────────────────────────────────────

export type FeeFrequency = 'annual' | 'quarterly' | 'monthly' | 'termly' | 'one_time';

export const FEE_FREQUENCY_LABELS: Record<FeeFrequency, string> = {
  annual: 'Annual',
  termly: 'Termly',
  quarterly: 'Quarterly',
  monthly: 'Monthly',
  one_time: 'One-time',
};

export const FEE_FREQUENCY_INSTALLMENTS: Record<FeeFrequency, number> = {
  one_time: 1,
  annual: 1,
  termly: 3,
  quarterly: 4,
  monthly: 12,
};

export interface FeePlanInstallment {
  dueDate: string;
  amount: number;
}

export interface FeePlanItem {
  feeCategoryId: string | { _id: string; name: string; code?: string; isOptional?: boolean; isRefundable?: boolean };
  amount: number;
  frequency: FeeFrequency;
  schedule: FeePlanInstallment[];
  isOptional: boolean;
}

export interface FeePlanRef {
  _id: string;
  name: string;
}

export interface FeePlanRow {
  _id: string;
  schoolId: string;
  sessionId: string;
  classIds: string[];
  sectionInstanceId?: string | null;
  session?: FeePlanRef | null;
  /** Populated class summaries — one entry per id in classIds. */
  classes: FeePlanRef[];
  section?: FeePlanRef | null;
  name: string;
  description?: string;
  itemCount: number;
  classCount: number;
  totalAnnualAmount: number;
  effectiveFrom: string;
  effectiveTo?: string | null;
  isActive: boolean;
  lockedAt?: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface FeePlan extends Omit<
  FeePlanRow,
  'itemCount' | 'classCount' | 'session' | 'classes' | 'section'
  | 'sessionId' | 'classIds' | 'sectionInstanceId'
> {
  items: FeePlanItem[];
  sessionId: string | { _id: string; name: string; startDate?: string; endDate?: string; status?: string };
  classIds: Array<string | { _id: string; name: string }>;
  sectionInstanceId?: string | { _id: string; name: string } | null;
}

export interface FeePlanItemInput {
  feeCategoryId: string;
  amount: number;
  frequency: FeeFrequency;
  isOptional?: boolean;
  /** Optional — server auto-generates if empty or if sum ≠ amount. */
  schedule?: FeePlanInstallment[];
}

export interface FeePlanInput {
  sessionId: string;
  /** Empty = school-wide. Length 1 optionally allows a section. */
  classIds: string[];
  sectionInstanceId?: string | null;
  name: string;
  description?: string;
  items: FeePlanItemInput[];
  effectiveFrom?: string;
  effectiveTo?: string | null;
}

export interface FeePlanListFilter {
  sessionId?: string;
  classId?: string | null;
  sectionInstanceId?: string | null;
  search?: string;
  isActive?: boolean;
  page?: number;
  limit?: number;
}

export interface FeePlanListResponse {
  plans: FeePlanRow[];
  pagination: {
    page: number;
    limit: number;
    totalItems: number;
    totalPages: number;
  };
}

// ── Fee Assignments ──────────────────────────────────────────────────────────

export type FeeAssignmentStatus = 'active' | 'suspended' | 'closed';
export type ProRationKind = 'none' | 'from_admission' | 'to_exit' | 'custom';

export interface FeeAssignmentRow {
  _id: string;
  schoolId: string;
  enrollmentId: string;
  studentId: string;
  sessionId: string;
  classId: string;
  sectionInstanceId?: string | null;
  feePlanId: string;
  student: { _id: string; name: string; grNumber?: string | null };
  rollNumber: number | null;
  class:   { _id: string; name: string | null };
  section: { _id: string; name: string | null } | null;
  plan:    { _id: string; name: string | null };
  itemCount: number;
  totalAnnualAmount: number;
  status: FeeAssignmentStatus;
  closedOn?: string | null;
  closureReason?: string;
  createdAt: string;
  updatedAt: string;
}

export interface FeeAssignmentListFilter {
  sessionId?: string;
  classId?: string;
  sectionInstanceId?: string | null;
  status?: FeeAssignmentStatus | 'any';
  feePlanId?: string;
  studentId?: string;
  search?: string;
  page?: number;
  limit?: number;
}

export interface FeeAssignmentListResponse {
  assignments: FeeAssignmentRow[];
  pagination: {
    page: number;
    limit: number;
    totalItems: number;
    totalPages: number;
  };
}

export interface BulkAssignPreview {
  plan: {
    _id: string;
    name: string;
    sessionId: string;
    classIds: string[];
    sectionInstanceId: string | null;
    totalAnnualAmount: number;
    itemCount: number;
    isLocked: boolean;
  };
  scope: {
    sessionId: string;
    classIds: string[];
    sectionInstanceId: string | null;
  };
  scopeNames: {
    session: { _id: string; name: string; startDate?: string; endDate?: string } | null;
    classes: Array<{ _id: string; name: string }>;
    section: { _id: string; name: string } | null;
  };
  eligibleCount: number;
  alreadyAssignedCount: number;
  newAssignmentsCount: number;
  totalBilling: number;
  alreadyAssigned: Array<{
    enrollmentId: string;
    studentId: string;
    studentName: string;
    rollNumber: number;
    existingPlanId: string;
    existingPlanName: string | null;
  }>;
}

export interface BulkAssignResult {
  created: number;
  skipped: number;
  planLocked: boolean;
  assignmentIds: string[];
}

// ── Fee Invoices ─────────────────────────────────────────────────────────────

export type FeeInvoiceStatus =
  | 'issued'
  | 'partial'
  | 'paid'
  | 'waived'
  | 'cancelled';

/** How each challan status reads, and its chip tone, everywhere it shows. */
export const FEE_INVOICE_STATUS_META: Record<FeeInvoiceStatus, { label: string; tone: 'success' | 'warning' | 'error' | 'info' | 'neutral' }> = {
  issued:    { label: 'Unpaid',    tone: 'warning' },
  partial:   { label: 'Part paid', tone: 'warning' },
  paid:      { label: 'Paid',      tone: 'success' },
  waived:    { label: 'Waived',    tone: 'neutral' },
  cancelled: { label: 'Cancelled', tone: 'neutral' },
};

/** A challan's voucher as printed — every figure worked out on the server (arrears, grand total, late fees). */
export interface FeeVoucherPrint {
  invoiceNumber: string;
  status: FeeInvoiceStatus;
  overdue: boolean;
  periodLabel: string;
  dueDate: string;
  issuedAt: string;
  sessionName: string | null;
  payer: { name: string; fatherName: string | null; address: string | null; grNumber: string | null; applicationNumber: string | null };
  cohort: { className: string | null; sectionName: string | null; rollNumber: number | null; planName: string | null };
  lines: Array<{ categoryName: string; gross: number; concession: number; waived: number; net: number }>;
  adjustments: Array<{ kind: FeeAdjustmentKind; amount: number }>;
  /** Earlier unpaid challans of the same student — present only while this one is open. */
  arrears: Array<{ invoiceNumber: string; periodLabel: string; outstanding: number }>;
  totals: {
    gross: number; concessions: number; waivers: number; lateFees: number; otherAdjustments: number;
    net: number; paid: number; outstanding: number; arrears: number; grandTotal: number;
  };
}

/** Vouchers with the school they are printed for. */
export interface FeeVoucherPrintSet {
  school: { name: string; address: string | null; phone: string | null; logo: string | null };
  currency: string;
  vouchers: FeeVoucherPrint[];
}

export type FeeInvoiceView = 'all' | 'outstanding' | 'overdue' | 'paid';

export interface FeeInvoicePeriod {
  label: string;
  startDate: string;
  endDate: string;
}

export interface FeeInvoiceTotals {
  gross: number;
  /** Denormalized sum of active FeeAdjustments (late fees, ad-hoc, corrections). */
  adjustments?: number;
  concessions: number;
  waivers: number;
  net: number;
  paid: number;
  outstanding: number;
}

export type FeeInvoiceKind = 'tuition' | 'application_fee' | 'admission_fee' | 'other';

export const FEE_INVOICE_KIND_LABEL: Record<FeeInvoiceKind, string> = {
  tuition: 'Tuition',
  application_fee: 'Application fee',
  admission_fee: 'Admission fee',
  other: 'Other',
};

export interface FeeInvoiceRow {
  _id: string;
  schoolId: string;
  /** Null on admission-fee invoices — they aren't assignment-driven. */
  assignmentId: string | null;
  enrollmentId: string | null;
  studentId: string | null;
  personId?: string | null;
  sessionId: string;
  classId: string | null;
  sectionInstanceId?: string | null;
  feePlanId: string | null;
  /** The invoice "kind" — tuition is the default; admission fees carry a
   *  distinct label so operators can scan what each row is for. */
  kind?: FeeInvoiceKind;
  /** Admission-fee backlink to the cycle; null on tuition. */
  cycleId?: string | null;
  /** Admission-fee backlink to the application; null on tuition. */
  applicationId?: string | null;
  student: {
    _id: string;
    name: string;
    grNumber?: string | null;
    /** An applicant's application number — they have no GR yet. */
    applicationNumber?: string | null;
    /** True when payer is an applicant (no Student record yet). */
    isApplicant?: boolean;
  };
  rollNumber: number | null;
  class: { _id: string; name: string | null } | null;
  section: { _id: string; name: string | null } | null;
  plan: { _id: string; name: string | null } | null;
  invoiceNumber: string;
  period: FeeInvoicePeriod;
  dueDate: string;
  totals: FeeInvoiceTotals;
  status: FeeInvoiceStatus;
  lineItemCount: number;
  issuedAt: string;
  paidAt?: string | null;
  cancellationReason?: string;
  isOverdue: boolean;
  /**
   * Read-time view of the student's OTHER unpaid challans issued before
   * this one. When rendered, printed alongside this challan's own line
   * items so the parent sees a single "grand total payable". Never
   * persisted; recomputed on every list read.
   */
  arrearsTotal?: number;
  arrearsCount?: number;
}

export interface FeeInvoiceLineItem {
  feeCategoryId: string | { _id: string; name?: string; code?: string };
  categoryName: string;
  grossAmount: number;
  concessionAmount: number;
  concessionRef?: string | null;
  waivedAmount: number;
  waiverRef?: string | null;
  netAmount: number;
}

export interface FeeInvoiceDetail extends Omit<
  FeeInvoiceRow,
  'student' | 'class' | 'section' | 'plan' | 'rollNumber' | 'lineItemCount' | 'isOverdue' |
  'studentId' | 'sessionId' | 'classId' | 'sectionInstanceId' | 'enrollmentId' | 'feePlanId' | 'personId'
> {
  studentId: string | { _id: string; name: string; fatherName?: string; address?: string };
  /** Payer for admission challans (no Student yet). */
  personId?: string | { _id: string; name: string; grNumber?: string } | null;
  sessionId: string | { _id: string; name: string };
  classId: string | { _id: string; name: string };
  sectionInstanceId?: string | { _id: string; name: string } | null;
  enrollmentId: string | { _id: string; rollNumber?: number };
  feePlanId: string | { _id: string; name: string };
  lineItems: FeeInvoiceLineItem[];
  adjustments?: FeeAdjustment[];
  waivers?: Array<{
    _id: string;
    lineItemIndex: number | null;
    amount: number;
    reason: string;
    createdAt: string;
    createdBy?: string | { _id: string; firstName?: string; lastName?: string } | null;
    isActive: boolean;
  }>;
  /**
   * Prior unpaid challans for the same student, oldest first. Read-time
   * derived — matches the arrears section on the printed voucher so the
   * dialog and the PDF never disagree.
   */
  arrears?: Array<{
    _id: string;
    invoiceNumber: string;
    period: { label: string; startDate?: string };
    totals: { outstanding: number };
    dueDate: string;
  }>;
}

export interface FeeInvoiceListFilter {
  sessionId?: string;
  classId?: string;
  sectionInstanceId?: string | null;
  studentId?: string;
  feePlanId?: string;
  feeAssignmentId?: string;
  status?: FeeInvoiceStatus | 'any';
  view?: FeeInvoiceView;
  periodLabel?: string;
  dueBefore?: string;
  dueAfter?: string;
  /** Free-text search — matches student name OR challan number. */
  search?: string;
  /** Dedicated GR Number filter — matches Student.grNumber. */
  grNumber?: string;
  page?: number;
  limit?: number;
}

export interface FeeInvoiceListResponse {
  invoices: FeeInvoiceRow[];
  kpis: { net: number; collected: number; outstanding: number; count: number };
  pagination: {
    page: number;
    limit: number;
    totalItems: number;
    totalPages: number;
  };
}

export interface GeneratePreview {
  period: FeeInvoicePeriod;
  /** When the challans will fall due: the chosen date, or issue day + the school's due days. */
  dueDate: string;
  activeAssignments: number;
  alreadyGenerated: number;
  willGenerate: number;
  projectedTotal: number;
}

export interface GenerateResult {
  period: FeeInvoicePeriod;
  created: number;
  skipped: number;
  invoiceIds: string[];
  totalGenerated: number;
}

export interface GenerateInput {
  sessionId: string;
  classId?: string | null;
  sectionInstanceId?: string | null;
  /** "YYYY-MM" */
  month: string;
  /** Optional. "YYYY-MM-DD". Defaults to now. */
  issueDate?: string | null;
  /** Optional. "YYYY-MM-DD". Defaults to issue day + the school's due days. */
  dueDate?: string | null;
}

// ── Fee Payments ─────────────────────────────────────────────────────────────

export type FeePaymentMethod = 'cash' | 'bank_transfer' | 'cheque' | 'online';
export type PaidByKind = 'applicant' | 'student' | 'parent' | 'guardian' | 'other';

export interface FeePaymentAllocationInput {
  invoiceId: string;
  amount: number;
}

export interface RecordFeePaymentInput {
  /** Person is the universal payer identity. Prefer this directly from
   *  callers that have it (admissions flow). */
  personId?: string;
  /** Fallback when the caller has a Student id — the server resolves it to
   *  the owning Person once. */
  studentId?: string;
  amount: number;
  method: FeePaymentMethod;
  receivedAt: string;                    // ISO
  paidBy: { kind: PaidByKind; name: string };
  allocations: FeePaymentAllocationInput[];
  /** Received beyond the challans paid — kept as the person's advance. */
  advanceAmount?: number;
  /** Paid out of the person's advance balance instead of new money. */
  fromAdvance?: number;
  referenceNumber?: string;
  externalReference?: string;
  notes?: string;
}

export interface FeePaymentRow {
  _id: string;
  schoolId: string;
  studentId: string;
  enrollmentId?: string | null;
  receiptNumber: string;
  amount: number;
  method: FeePaymentMethod;
  receivedAt: string;
  referenceNumber?: string;
  paidBy: { kind: PaidByKind; name: string };
  allocationCount: number;
  /** Kept as advance / paid from advance on this receipt. */
  advanceAmount?: number;
  fromAdvance?: number;
  notes?: string;
  isActive: boolean;
  reversalReason?: string;
  /** `applicationNumber`: set for an admission-fee payment, naming the applicant. */
  student: { _id: string; name: string; grNumber?: string | null; applicationNumber?: string | null };
  rollNumber: number | null;
  createdAt: string;
  updatedAt: string;
}

export interface FeePaymentDetail {
  _id: string;
  schoolId: string;
  studentId: string | { _id: string; name: string; fatherName?: string } | null;
  /** The payer's record — applicants have one before they have a Student. */
  personId?: string | { _id: string; name: string; grNumber?: string };
  /** The application an admission-fee payment was for; null for tuition. */
  applicationNumber?: string | null;
  classId?: string | { _id: string; name: string } | null;
  enrollmentId?: string | { _id: string; rollNumber?: number } | null;
  receiptNumber: string;
  amount: number;
  method: FeePaymentMethod;
  receivedAt: string;
  referenceNumber?: string;
  paidBy: { kind: PaidByKind; name: string };
  notes?: string;
  /** Kept as the person's advance / paid out of their advance. */
  advanceAmount?: number;
  fromAdvance?: number;
  allocations: Array<{
    invoiceId: string;
    amount: number;
    invoice?: {
      _id: string;
      invoiceNumber: string;
      kind?: FeeInvoiceKind;
      period: { label: string };
      totals: FeeInvoiceTotals;
      status: FeeInvoiceStatus;
    } | null;
  }>;
  isActive: boolean;
  reversalReason?: string;
  reversedBy?: string | { _id: string; name?: string } | null;
  reversedAt?: string | null;
  collectedBy?: string | { _id: string; name?: string; email?: string };
  createdAt: string;
  updatedAt: string;
}

export interface FeePaymentListFilter {
  studentId?: string;
  method?: FeePaymentMethod;
  includeReversed?: boolean;
  dateFrom?: string;
  dateTo?: string;
  search?: string;
  page?: number;
  limit?: number;
}

export interface FeePaymentListResponse {
  payments: FeePaymentRow[];
  kpis: { collected: number; count: number };
  pagination: {
    page: number;
    limit: number;
    totalItems: number;
    totalPages: number;
  };
}

export interface CollectibleInvoice {
  _id: string;
  invoiceNumber: string;
  period: FeeInvoicePeriod;
  dueDate: string;
  totals: FeeInvoiceTotals;
  status: FeeInvoiceStatus;
  classId?: string;
  sectionInstanceId?: string | null;
}

export interface CollectibleResponse {
  student: {
    _id: string;
    name: string;
    fatherName?: string;
    grNumber?: string | null;
    /** Set for an applicant (no Student yet) — their latest application's number. */
    applicationNumber?: string | null;
    rollNumber: number | null;
    class: { _id: string; name: string } | null;
    section: { _id: string; name: string } | null;
  };
  totalOutstanding: number;
  /** Advance this person holds, available to pay challans. */
  advanceBalance?: number;
  invoices: CollectibleInvoice[];
}

export interface StudentWithDues {
  /** Universal payer identity. Always set. */
  personId: string | null;
  /** Null when the payer is an applicant (no Student record yet). */
  studentId: string | null;
  name: string;
  fatherName?: string | null;
  grNumber?: string | null;
  /** An applicant's latest application number — they have no GR yet. */
  applicationNumber?: string | null;
  /** True when the payer is an applicant, false for enrolled students. */
  isApplicant?: boolean;
  rollNumber: number | null;
  class: { _id: string; name: string | null } | null;
  section: { _id: string; name: string | null } | null;
  totalOutstanding: number;
  invoiceCount: number;
  oldestDue: string | null;
  isOverdue: boolean;
}

export interface StudentsWithDuesFilter {
  sessionId?: string;
  classId?: string;
  sectionInstanceId?: string | null;
  search?: string;
  overdueOnly?: boolean;
  page?: number;
  limit?: number;
}

export interface StudentsWithDuesResponse {
  students: StudentWithDues[];
  pagination: {
    page: number;
    limit: number;
    totalItems: number;
    totalPages: number;
  };
}

export interface RecordFeePaymentResult {
  payment: FeePaymentRow;
  receiptNumber: string;
}

// ── Student fee summary (drawer) ─────────────────────────────────────────────

export interface StudentFeeSummary {
  student: {
    _id: string;
    name: string;
    fatherName?: string;
    grNumber?: string | null;
    rollNumber: number | null;
    class: { _id: string; name: string } | null;
    section: { _id: string; name: string } | null;
  };
  kpis: {
    billed: number;
    collected: number;
    outstanding: number;
    /** Advance the family holds, available against the next challans. */
    advanceHeld: number;
    /** Given back through refunds and returned deposits. */
    refunded: number;
    invoiceCount: number;
    outstandingInvoiceCount: number;
    overdueInvoiceCount: number;
  };
  /** Active plan assignment for this student. Null when no plan is attached. */
  assignment: {
    _id: string;
    feePlanId: string;
    planName: string | null;
    totalAnnualAmount: number;
    sessionId: string;
    status: FeeAssignmentStatus;
  } | null;
  outstandingInvoices: Array<{
    _id: string;
    invoiceNumber: string;
    period: FeeInvoicePeriod;
    dueDate: string;
    totals: FeeInvoiceTotals;
    status: FeeInvoiceStatus;
    isOverdue: boolean;
  }>;
  recentPayments: Array<{
    _id: string;
    receiptNumber: string;
    amount: number;
    method: FeePaymentMethod;
    receivedAt: string;
    referenceNumber?: string;
    paidBy: { kind: PaidByKind; name: string };
    allocations: Array<{ invoiceId: string; amount: number }>;
    }>;
}

// ── Student fee activity feed ────────────────────────────────────────────────

export type FeeActivityKind =
  | 'invoice_issued'
  | 'invoice_cancelled'
  | 'payment_received'
  | 'payment_reversed'
  | 'adjustment_added'
  | 'adjustment_revoked'
  | 'waiver_applied'
  | 'waiver_revoked'
  | 'concession_created'
  | 'concession_revoked';

export interface FeeActivityEvent {
  kind: FeeActivityKind;
  at: string;
  invoice?: {
    _id: string;
    invoiceNumber: string;
    period: { label: string };
    net: number;
    status: string;
  };
  payment?: {
    _id: string;
    receiptNumber: string;
    amount: number;
    method: FeePaymentMethod;
    referenceNumber?: string;
    paidBy?: { kind: PaidByKind; name: string };
    allocationCount: number;
  };
  adjustment?: {
    _id: string;
    invoiceId: string;
    invoiceNumber: string | null;
    kind: FeeAdjustmentKind;
    amount: number;
    reason: string;
  };
  waiver?: {
    _id: string;
    invoiceId: string;
    invoiceNumber: string | null;
    amount: number;
    reason: string;
    lineItemIndex: number | null;
  };
  concession?: {
    _id: string;
    label: string;
    mode: 'percent' | 'flat';
    amount: number;
    appliesTo: 'all' | 'categories';
    validFrom: string;
    validTo: string | null;
  };
  reason?: string;
}

export interface FeeActivityResponse {
  events: FeeActivityEvent[];
}

// ── Fee reports ──────────────────────────────────────────────────────────────

export interface AgingBucket {
  key: 'current' | '1_30' | '31_60' | '61_90' | '90_plus';
  label: string;
  outstanding: number;
  count: number;
}

export interface AgingGroup {
  id: string | null;
  name: string;
  outstanding: number;
  count: number;
}

export interface AgingResponse {
  asOfDate: string;
  buckets: AgingBucket[];
  byGroup: AgingGroup[];
  total: { outstanding: number; count: number };
}

export interface CollectionRateGroup {
  id: string | null;
  name: string;
  billed: number;
  collected: number;
  outstanding: number;
  rate: number;      // 0..1
  count: number;
}

export interface CollectionRateResponse {
  window: { from: string | null; to: string | null };
  total: { billed: number; collected: number; outstanding: number; invoiceCount: number; rate: number };
  byGroup: CollectionRateGroup[];
}

export interface ByCategoryRow {
  id: string | null;
  name: string;
  billed: number;
  collected: number;
  outstanding: number;
  invoiceCount: number;
}

export interface ByCategoryResponse {
  window: { from: string | null; to: string | null };
  byCategory: ByCategoryRow[];
  total: { billed: number; collected: number; outstanding: number };
}

export interface ForecastDay {
  date: string;    // YYYY-MM-DD
  amount: number;
  invoiceCount: number;
}

export interface ForecastResponse {
  window: { from: string; to: string; horizonDays: number };
  upcoming: ForecastDay[];
  total: { amount: number; invoiceCount: number };
}

// ── Collection trend ─────────────────────────────────────────────────────────

export interface CollectionTrendMonth {
  month: string;   // "YYYY-MM"
  label: string;   // "Aug 2026"
  amount: number;
  count: number;
}

export interface CollectionTrendResponse {
  window: { months: number; from: string; to: string };
  trend: CollectionTrendMonth[];
  total: { amount: number; count: number };
  /** MoM change in the last bucket vs. previous bucket. Range: -1..∞ */
  momChange: number;
}

// ── Top outstanding students ────────────────────────────────────────────────

export interface TopOutstandingStudent {
  studentId: string;
  studentName: string;
  grNumber?: string | null;
  rollNumber: number | null;
  className: string | null;
  sectionName: string | null;
  outstanding: number;
  invoiceCount: number;
  oldestDueDate: string | null;
  oldestOverdueDays: number;
}

export interface TopOutstandingStudentsResponse {
  students: TopOutstandingStudent[];
}

// ── Late fees + adjustments ──────────────────────────────────────────────────

export type FeeAdjustmentKind = 'late_fee' | 'ad_hoc_charge' | 'correction' | 'other' | 'write_off';

export const ADJUSTMENT_KIND_LABEL: Record<FeeAdjustmentKind, string> = {
  late_fee:      'Late fee',
  ad_hoc_charge: 'Extra charge',
  correction:    'Correction',
  other:         'Other',
  write_off:     'Written off (bad debt)',
};

export interface FeeAdjustment {
  _id: string;
  invoiceId: string;
  studentId: string;
  kind: FeeAdjustmentKind;
  amount: number;
  reason: string;
  meta?: {
    daysOverdue?: number;
    policySnapshot?: {
      kind: string;
      amount: number;
      cap: number | null;
      graceDays: number;
    };
  };
  createdBy?: string | { _id: string; firstName?: string; lastName?: string } | null;
  createdAt: string;
  isActive: boolean;
  revokedAt?: string | null;
  revocationReason?: string;
}

export interface LateFeePreview {
  enabled: boolean;
  policySnapshot: null | {
    kind: 'flat' | 'percent' | 'daily_flat' | 'daily_percent';
    amount: number;
    cap: number | null;
    graceDays: number;
  };
  asOfDate: string;
  eligibleCount: number;
  skippedAlreadyCharged: number;
  totalCharge: number;
  sampleInvoiceIds: string[];
}

export interface LateFeeApplyResult {
  processed: number;
  totalCharged: number;
  adjustmentIds: string[];
  skippedAlreadyCharged: number;
}

// ── Adjustments summary (reports strip) ─────────────────────────────────────

export interface AdjustmentsSummaryResponse {
  totals: { grossBilled: number; netBilled: number };
  concessions: { amount: number };
  waivers: { amount: number; count: number };
  lateFees: { amount: number; count: number };
  adjustmentsAdHoc: { amount: number; count: number };
}

// ── Payment method mix ──────────────────────────────────────────────────────

export type FeePaymentMethodKey = 'cash' | 'bank_transfer' | 'cheque' | 'online';

export interface PaymentMethodRow {
  method: FeePaymentMethodKey;
  amount: number;
  count: number;
}

export interface PaymentMethodMixResponse {
  window: { from: string | null; to: string | null };
  byMethod: PaymentMethodRow[];
  total: { amount: number; count: number };
}

// ── Bank reconciliation ────────────────────────────────────────────────────

export type ReconciliationExceptionCode =
  | 'INVALID_CHALLAN_FORMAT'
  | 'INVALID_AMOUNT'
  | 'INVALID_DATE'
  | 'MISSING_BANK_REF'
  | 'CHALLAN_NOT_FOUND'
  | 'CHALLAN_CANCELLED'
  | 'CHALLAN_WAIVED'
  | 'ALREADY_PAID'
  | 'AMOUNT_MISMATCH'
  | 'DUPLICATE_BANK_REF'
  | 'COMMIT_FAILED';

export interface BankRow {
  challanNumber: string;
  /** A number, or the file's own text when it isn't one (the server reports it back as invalid). */
  amount: number | string;
  txnDate: string;
  bankRef: string;
  memo?: string;
  paidByName?: string;
}

export interface ReconciliationRowResult {
  index: number;
  challanNumber: string;
  amount: number;
  status: 'matched' | 'exception';
  exceptionCode?: ReconciliationExceptionCode;
  exceptionMessage?: string;
  invoiceId?: string;
  outstanding?: number;
  studentName?: string;
  /**
   * Set on matched rows whose amount covered prior unpaid challans. The
   * payment was allocated oldest-first across those + this challan.
   */
  arrearsCount?: number;
  arrearsAmount?: number;
  receiptNumber?: string;
  paymentId?: string;
}

export interface ReconciliationResult {
  totalRows: number;
  matched: number;
  exceptions: number;
  totalAmount: number;
  matchedAmount: number;
  rows: ReconciliationRowResult[];
}
